import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { useConversationsQuery } from "../api/messages";
import { Link } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { useMyHomeQuery, usePresenceQuery } from "../api/queries";
import { useTownSession } from "../app/TownSessionContext";
import type { TownSession } from "../app/townSession";
import { readJson, writeJson } from "../lib/storage";
import { selectHereNow, useTownStore } from "../state/store";
import { AccountMenu } from "./AccountMenu";
import { StoreTasksButton } from "./rewards/StoreTasks";
import { WalletChips } from "./WalletChips";
import { WorldChip } from "./worlds/WorldsDialog";
import { useMediaQuery } from "../lib/useMediaQuery";

/**
 * World HUD (docs/01 section 6): Townsy identity, Overview/Walk toggle, Online indicator,
 * Statistics, Directory, chat toggle, Coins + Credit (two separately labelled chips) or a guest
 * sign-up link, account/guest menu, Home control in overview, "Plot listings" inside the Real
 * Estate office, "Home Builders" / "Furnishings" inside those shops, "My home" for residents
 * who own a home, "Get out" while driving a town car (ADR-0006), and the Map toggle on the street
 * (ADR-0008; the choice is remembered per viewer).
 *
 * One continuous town (ADR-0042 decisions 1 and 4): the whole town is one street room, so the pill counts the people
 * "on the street"; in Overview on the street a small strip under the bar frames each district ("Central · Riverside ·
 * Hillcrest", `session.frameDistrict`) and shows a dismissible tip about moving the camera.
 */
export function Hud({ below }: { below?: ReactNode } = {}) {
  const session = useTownSession();
  const {
    mode,
    sceneStatus,
    chatOpen,
    statsOpen,
    directoryOpen,
    transitionPending,
    roomKind,
    roomStatus,
    testMode,
    roomPropertyId,
    realEstateOpen,
    homeBuildersOpen,
    furnishingsOpen,
    myHomeOpen,
    signedIn,
    driving,
    vehiclePending,
    minimapOpen,
  } = useTownStore(
    useShallow((s) => ({
      minimapOpen: s.ui.minimapOpen,
      driving: s.ui.drivingVehicleId !== null,
      vehiclePending: s.ui.vehiclePending,
      roomPropertyId: s.room.propertyId,
      realEstateOpen: s.ui.realEstateOpen,
      homeBuildersOpen: s.ui.homeBuildersOpen,
      furnishingsOpen: s.ui.furnishingsOpen,
      myHomeOpen: s.ui.myHomeOpen,
      signedIn: Boolean(s.bootstrap?.session.userId),
      mode: s.ui.mode,
      sceneStatus: s.ui.sceneStatus,
      chatOpen: s.ui.chatOpen,
      statsOpen: s.ui.statsOpen,
      directoryOpen: s.ui.directoryOpen,
      transitionPending: s.ui.transitionPending,
      roomKind: s.room.kind,
      roomStatus: s.room.status,
      testMode: s.bootstrap?.environment !== "production",
    })),
  );
  const unread = useTownStore((s) => s.chat.unread);
  const hereNow = useTownStore(selectHereNow);
  const toggleStats = useTownStore((s) => s.toggleStats);
  const toggleDirectory = useTownStore((s) => s.toggleDirectory);
  const toggleChat = useTownStore((s) => s.toggleChat);
  const setUi = useTownStore((s) => s.setUi);
  const setMinimapOpen = useTownStore((s) => s.setMinimapOpen);
  const civic = roomKind === "interior" && roomPropertyId ? (session?.civicKind(roomPropertyId) ?? null) : null;
  const inOffice = civic === "real_estate_office";
  const myHome = useMyHomeQuery(signedIn);
  const ownsHome = Boolean(myHome.data?.home);
  const presence = usePresenceQuery(true);
  const sceneReady = sceneStatus === "ready";
  const joined = roomStatus === "joined";
  const barRef = useRef<HTMLDivElement | null>(null);
  const compact = useMediaQuery(COMPACT_HUD_QUERY);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const messagesUnread = useMessagesUnread();
  const menuUnread = unread + messagesUnread;
  const roomKey = useTownStore((s) => s.room.roomKey);

  // The phone menu closes on Escape, a tap outside it, a room change, and when the screen leaves the phone layout.
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (moreRef.current?.contains(t) || barRef.current?.contains(t)) return;
      setMoreOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [moreOpen]);
  useEffect(() => setMoreOpen(false), [roomKey, compact]);

  // The first presence fetch lands before this visitor's own room heartbeat, so the pill would
  // read "0 online · 1 here" for up to 30 s; refresh once the API's 5 s presence cache has rolled.
  const refetchPresence = presence.refetch;
  useEffect(() => {
    if (!joined) return;
    const t = setTimeout(() => void refetchPresence(), 6000);
    return () => clearTimeout(t);
  }, [joined, refetchPresence]);

  // Side panels, sheets and the chat are laid out below the HUD (`--hud-height`, 52 px for one row). The
  // bar wraps onto a second row when a resident inside a shop has more buttons than fit (e.g. 1366 px:
  // Exit building + Furnishings + My home); keep the variable equal to the bar's real bottom edge so the
  // coin chip, Statistics, Directory and Chat never end up hidden behind a side panel.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--hud-height", `${Math.ceil(el.offsetTop + el.offsetHeight)}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--hud-height");
    };
  }, []);

  const homeButton =
    mode === "overview" ? (
      <button type="button" className="btn btn-sm" disabled={!sceneReady} onClick={() => session?.resetOverview()} title="Return to the starting view">
        Home
      </button>
    ) : null;
  const exitButton =
    roomKind === "interior" ? (
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={!joined || transitionPending}
        onClick={() => session?.exitInterior()}
        aria-label={compact ? "Exit building" : undefined}
      >
        {compact ? "Exit" : "Exit building"}
      </button>
    ) : null;
  const shopButtons = (
    <>
      {roomKind === "interior" ? <StoreTasksButton /> : null}
      {inOffice ? (
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={realEstateOpen && !myHomeOpen}
          onClick={() => setUi(realEstateOpen && !myHomeOpen ? { realEstateOpen: false } : { realEstateOpen: true, myHomeOpen: false })}
          data-testid="hud-real-estate"
        >
          Plot listings
        </button>
      ) : null}
      {civic === "home_builders" ? (
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={homeBuildersOpen && !myHomeOpen}
          onClick={() => setUi(homeBuildersOpen && !myHomeOpen ? { homeBuildersOpen: false } : { homeBuildersOpen: true, myHomeOpen: false })}
          data-testid="hud-home-builders"
        >
          Home Builders
        </button>
      ) : null}
      {civic === "furnishings" ? (
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={furnishingsOpen && !myHomeOpen}
          onClick={() => setUi(furnishingsOpen && !myHomeOpen ? { furnishingsOpen: false } : { furnishingsOpen: true, myHomeOpen: false })}
          data-testid="hud-furnishings"
        >
          Furnishings
        </button>
      ) : null}
    </>
  );
  const myHomeButton = ownsHome ? (
    <button
      type="button"
      className="btn btn-sm"
      aria-pressed={myHomeOpen}
      onClick={() => (myHomeOpen ? setUi({ myHomeOpen: false }) : session ? session.openMyHome() : setUi({ myHomeOpen: true }))}
      data-testid="hud-my-home"
    >
      My home
    </button>
  ) : null;
  const getOutButton =
    roomKind === "street" && driving ? (
      <button
        type="button"
        className="btn btn-sm btn-primary"
        disabled={!joined || vehiclePending === "exit" || !session}
        onClick={() => session?.exitVehicle()}
        aria-label={vehiclePending === "exit" ? "Stopping the car to get out" : "Get out of the car (E)"}
        title={vehiclePending === "exit" ? "Stopping the car…" : "Stops the car and lets you out by the driver's door (E)"}
        data-testid="hud-get-out"
      >
        {vehiclePending === "exit" ? "Stopping…" : compact ? "Get out" : "Get out (E)"}
      </button>
    ) : null;
  const plazaButton = roomKind === "street" && mode === "walk" ? <ReturnToPlazaButton /> : null;
  const pill = presencePill({
    online: presence.data?.online ?? null,
    here: joined ? hereNow : null,
    onStreet: roomKind === "street",
  });
  const onlinePill = (
    <span className="online-pill" title={pill.title} data-testid="hud-presence">
      <span className={`online-dot${presence.data ? "" : " offline"}`} aria-hidden="true" />
      <span>{pill.text}</span>
    </span>
  );
  // ADR-0042 decision 4: district framing and the camera tip, in Overview on the street (the free overview camera).
  const overviewTools = roomKind === "street" && mode === "overview" && sceneReady ? <OverviewTools session={session} /> : null;
  const panelButtons = (
    <>
      <button type="button" className="btn btn-sm" aria-pressed={statsOpen} onClick={() => toggleStats()}>
        Statistics
      </button>
      <button type="button" className="btn btn-sm" aria-pressed={directoryOpen} onClick={() => toggleDirectory()}>
        Directory
      </button>
      {roomKind === "street" && sceneReady ? (
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={minimapOpen}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setMinimapOpen(!minimapOpen)}
          title={minimapOpen ? "Hide the town map" : "Show a small map of the town"}
          data-testid="hud-map"
        >
          Map
        </button>
      ) : null}
      <button type="button" className="btn btn-sm" aria-pressed={chatOpen} onClick={() => toggleChat()} aria-label={unread ? `Chat, ${unread} unread` : "Chat"}>
        Chat{unread > 0 ? <span className="badge badge-coin">{unread}</span> : null}
      </button>
      <MessagesButton />
    </>
  );
  const identity = (
    <>
      <Link className="wordmark" to="/" aria-label="Townsy home">
        Townsy
      </Link>
      {testMode && !compact ? (
        <span className="badge badge-warning" title="Development environment: prices and data are test values">
          Test
        </span>
      ) : null}
      <div className="segmented" role="group" aria-label="Camera mode">
        <button
          type="button"
          className="btn btn-sm"
          aria-pressed={mode === "overview"}
          disabled={!sceneReady}
          onClick={() => session?.setMode("overview")}
        >
          Overview
        </button>
        <button type="button" className="btn btn-sm" aria-pressed={mode === "walk"} disabled={!sceneReady} onClick={() => session?.setMode("walk")}>
          Walk
        </button>
      </div>
    </>
  );

  if (compact) {
    // Phones and short landscape screens (founder 2026-10-14: "make sure this game can work on Tablet/Mobile devices
    // nicely"): one row with the camera mode and the one action that matters right now (Exit building / Get out);
    // everything else is one tap away in Menu, so the bar never covers a third of the town.
    return (
      <header className={`hud hud-compact${moreOpen ? " hud-menu-open" : ""}`}>
        <div className="hud-bar" ref={barRef}>
          {identity}
          {exitButton}
          {getOutButton}
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-sm hud-menu-btn"
            aria-expanded={moreOpen}
            aria-controls="hud-more"
            aria-label={menuUnread ? `Menu, ${menuUnread} unread` : "Menu"}
            onClick={() => setMoreOpen(!moreOpen)}
            data-testid="hud-menu"
          >
            Menu{menuUnread > 0 ? <span className="badge badge-coin">{menuUnread}</span> : null}
          </button>
        </div>
        {moreOpen ? (
          <div
            id="hud-more"
            className="hud-more"
            ref={moreRef}
            data-testid="hud-more"
            onClick={(e) => {
              const t = e.target as HTMLElement;
              if (t.closest("button, a") && !t.closest("[data-keep-open]")) setMoreOpen(false);
            }}
          >
            <div className="hud-more-top">
              {onlinePill}
              <WorldChip />
              <div data-keep-open>
                <AccountMenu />
              </div>
            </div>
            <div className="hud-more-wallet">
              <WalletChips />
            </div>
            <div className="hud-more-grid">
              {homeButton}
              {plazaButton}
              {myHomeButton}
              {shopButtons}
              {panelButtons}
            </div>
          </div>
        ) : (
          overviewTools
        )}
        {below ? <div className="hud-below">{below}</div> : null}
      </header>
    );
  }

  return (
    <header className="hud">
      <div className="hud-bar" ref={barRef}>
        {identity}
        {homeButton}
        {exitButton}
        {shopButtons}
        {myHomeButton}
        {getOutButton}
        {plazaButton}
        <span className="spacer" />
        {onlinePill}
        <WorldChip />
        <div className="hud-actions">
          {panelButtons}
          <WalletChips />
          <AccountMenu />
        </div>
      </div>
      {overviewTools}
      {below ? <div className="hud-below">{below}</div> : null}
    </header>
  );
}

/** Phones (portrait) and short landscape screens get the one-row bar with a Menu. */
export const COMPACT_HUD_QUERY = "(max-width: 640px), (max-height: 500px)";

/**
 * The presence pill (ADR-0042 decision 1: one street room for the whole town): "12 online · 3 on the street". "online"
 * is the town-wide count from the API (null before it loads: "Online —"); the second count is the current room's
 * people (this client's room state, AI residents excluded), null until the room is joined. On a street that room is
 * the one town street (every district); inside a building it is that building's room: "12 online · 2 here".
 */
export function presencePill({ online, here, onStreet }: { online: number | null; here: number | null; onStreet: boolean }): {
  text: string;
  title: string;
} {
  const text = `${online !== null ? `${online} online` : "Online —"}${here !== null ? ` · ${here} ${onStreet ? "on the street" : "here"}` : ""}`;
  const room = onStreet ? "On the street: the people outside in the town right now (one street for every district)." : "Here: the people in this building right now.";
  return {
    text,
    title: `Online: distinct visitors with a live connection in the last 45 seconds; test traffic excluded. ${room}`,
  };
}

/** The Overview camera tip is remembered once dismissed (a per-viewer convenience; storage may be missing or throw). */
export const OVERVIEW_TIP_STORAGE_KEY = "townsy.overviewTip.v1";
/** The tip (ADR-0042 decision 4). Touch-only screens drag with a finger and have no arrow keys. */
export const OVERVIEW_TIP = "Drag or use the arrow keys to move around";
export const OVERVIEW_TIP_TOUCH = "Drag to move around";
const TOUCH_ONLY_QUERY = "(hover: none) and (pointer: coarse)";

/**
 * `session.frameDistrict(id)` (ADR-0042 slice 3: the free overview camera frames a district from above), bound to the
 * session, or null while the session has no such method. Without it the HUD shows neither the district buttons nor
 * the tip, since both are about that camera.
 */
export function districtFramer(session: TownSession | null): ((districtId: string) => void) | null {
  const fn = (session as unknown as { frameDistrict?: unknown } | null)?.frameDistrict;
  return typeof fn === "function" ? (districtId) => void (fn as (id: string) => unknown).call(session, districtId) : null;
}

/** The Overview strip under the bar: the district buttons (manifest order) and the camera tip until it is dismissed. */
function OverviewTools({ session }: { session: TownSession | null }) {
  const frame = districtFramer(session);
  const touch = useMediaQuery(TOUCH_ONLY_QUERY);
  const [tipDismissed, setTipDismissed] = useState(() => readJson<boolean>("local", OVERVIEW_TIP_STORAGE_KEY) === true);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const shown = Boolean(frame && session);
  // The phone map sits under the HUD (top right): `--hud-overview-space` keeps it below this strip while it shows.
  useLayoutEffect(() => {
    const el = stripRef.current;
    if (!shown || !el) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--hud-overview-space", `${Math.ceil(el.offsetHeight + 6)}px`);
    apply();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--hud-overview-space");
    };
  }, [shown]);
  if (!frame || !session) return null;
  const districts = session.manifest.districts;
  return (
    <OverviewToolsView
      stripRef={stripRef}
      districts={districts.length > 1 ? districts : []}
      onFrame={frame}
      tip={tipDismissed ? null : touch ? OVERVIEW_TIP_TOUCH : OVERVIEW_TIP}
      onDismissTip={() => {
        writeJson("local", OVERVIEW_TIP_STORAGE_KEY, true);
        setTipDismissed(true);
      }}
    />
  );
}

/**
 * The Overview strip, presentational (server-rendered in the unit tests): "Central · Riverside · Hillcrest" frame that
 * district from above; the tip has its own ×. Renders nothing when there is nothing to show.
 */
export function OverviewToolsView({
  districts,
  onFrame,
  tip,
  onDismissTip,
  stripRef,
}: {
  districts: readonly { id: string; name: string }[];
  onFrame(districtId: string): void;
  tip: string | null;
  onDismissTip(): void;
  stripRef?: Ref<HTMLDivElement>;
}) {
  if (districts.length === 0 && !tip) return null;
  return (
    <div className="hud-overview" ref={stripRef} data-testid="hud-overview">
      {districts.length > 0 ? (
        <div className="segmented" role="group" aria-label="Show a district from above" data-testid="hud-districts">
          {districts.map((d) => (
            <button
              key={d.id}
              type="button"
              className="btn btn-sm"
              aria-label={`Show ${d.name} from above`}
              title={`Show ${d.name} from above`}
              // A press never takes keyboard focus from the town: the arrow keys keep moving the camera.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onFrame(d.id)}
              data-testid={`hud-frame-${d.id}`}
            >
              {d.name}
            </button>
          ))}
        </div>
      ) : null}
      {tip ? (
        <span className="hud-overview-tip" role="note" data-testid="hud-overview-tip">
          <span>{tip}</span>
          <button type="button" className="btn btn-ghost btn-sm icon-btn" aria-label="Dismiss the tip" title="Dismiss the tip" onClick={onDismissTip} data-testid="hud-overview-tip-dismiss">
            ×
          </button>
        </span>
      ) : null}
    </div>
  );
}

/** Unread private messages (0 when they are off or the visitor is a guest), for the phone Menu's badge. */
function useMessagesUnread(): number {
  const enabled = useTownStore((s) => Boolean(s.bootstrap?.features.privateMessagesEnabled && s.bootstrap.session.userId));
  const list = useConversationsQuery(enabled);
  return enabled ? (list.data?.unreadTotal ?? 0) : 0;
}

/** Private messages (ADR-0014): signed-in accounts where the server has them on; unread count from the API. */
function MessagesButton() {
  const enabled = useTownStore((s) => Boolean(s.bootstrap?.features.privateMessagesEnabled && s.bootstrap.session.userId));
  const open = useTownStore((s) => Boolean(s.ui.messages));
  const list = useConversationsQuery(enabled);
  if (!enabled) return null;
  const unread = list.data?.unreadTotal ?? 0;
  return (
    <button
      type="button"
      className="btn btn-sm"
      aria-pressed={open}
      onClick={() => (open ? useTownStore.getState().closeMessages() : useTownStore.getState().openMessages())}
      aria-label={unread ? `Messages, ${unread} unread` : "Messages"}
      data-testid="hud-messages"
    >
      Messages{unread > 0 ? <span className="badge badge-coin">{unread}</span> : null}
    </button>
  );
}

function ReturnToPlazaButton() {
  const session = useTownSession();
  const until = useTownStore((s) => s.ui.returnCooldownUntil);
  const joined = useTownStore((s) => s.room.status === "joined");
  const driving = useTownStore((s) => s.ui.drivingVehicleId !== null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until <= Date.now()) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [until]);
  const left = Math.max(0, Math.ceil((until - now) / 1000));
  return (
    <button
      type="button"
      className="btn btn-sm"
      disabled={!joined || left > 0 || !session}
      onClick={() => session?.returnToPlaza()}
      title={
        driving
          ? "Server-side recovery: gets you out of the car, then teleports you to a free spot on the plaza (10 s cooldown)"
          : "Server-side recovery: teleports you to a free spot on the plaza (10 s cooldown)"
      }
    >
      {left > 0 ? `Plaza (${left}s)` : "Return to plaza"}
    </button>
  );
}
